import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DateField, FileField, FormErrorSummary, M, MoneyField, TextField, TextareaField, applyServerErrors, zOptionalMoney } from '@/business';
import { Button, Dialog, DialogContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';
import { addDaysToDateOnly, todayInDubai } from '@/shared/lib/dates';

import { useSettlementCommand, type SettlementCommand } from '../../api/settlementMutations';

export type SettlementAction = SettlementCommand;

/** Inputs per settlement command (POST …/settlement/items/{itemId}/commands/*). */
const FIELDS: Record<SettlementAction, ('issuedOn' | 'expiresOn' | 'reference' | 'amount' | 'date' | 'file')[]> = {
  request_letter: [],
  request_reissue: [],
  mark_letter_used: [],
  record_letter: ['issuedOn', 'expiresOn', 'reference', 'amount', 'file'],
  record_liability: ['amount'],
  issue_mc: ['reference', 'date', 'file'],
  record_settled: ['date', 'amount'],
  record_clearance: ['date', 'file'],
};

const schema = z.object({ issuedOn: z.string().optional(), expiresOn: z.string().optional(), reference: z.string().optional(), amount: zOptionalMoney({ min: '1.00' }), date: z.string().optional(), note: z.string().optional(), action: z.string() }).superRefine((v, ctx) => {
  const f = FIELDS[v.action as SettlementAction];
  if (f.includes('issuedOn') && !v.issuedOn) ctx.addIssue({ code: 'custom', path: ['issuedOn'], message: M.required });
  if (f.includes('expiresOn') && !v.expiresOn) ctx.addIssue({ code: 'custom', path: ['expiresOn'], message: M.required });
  if (v.action === 'issue_mc' && !v.reference?.trim()) ctx.addIssue({ code: 'custom', path: ['reference'], message: M.required });
  if (v.action === 'record_liability' && !v.amount) ctx.addIssue({ code: 'custom', path: ['amount'], message: M.required });
});
type Values = z.infer<typeof schema>;

/** One step of a settlement item: letter requested / issued (with expiry), MC issued, settled, clearance received. */
export function SettlementEventDialog({ dealId, item, action, onClose }: { dealId: string; item: Schema<'SettlementItem'>; action: SettlementAction; onClose: () => void }) {
  const { t } = useTranslation('deals');
  const save = useSettlementCommand(dealId);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const today = todayInDubai();
  const f = FIELDS[action];
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { action, issuedOn: today, expiresOn: addDaysToDateOnly(today, 15), reference: '', amount: item.liabilityAmount, date: today, note: '' } });
  const vars = { bank: item.bank.code };
  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await save.mutateAsync({
        itemId: item.id,
        command: action,
        file: f.includes('file') ? file : null,
        body: {
          ...(f.includes('issuedOn') && v.issuedOn ? { issuedOn: v.issuedOn } : {}),
          ...(f.includes('expiresOn') && v.expiresOn ? { expiresOn: v.expiresOn } : {}),
          ...(f.includes('reference') && v.reference?.trim() ? { reference: v.reference.trim() } : {}),
          ...(f.includes('amount') && v.amount ? { amount: v.amount } : {}),
          ...(f.includes('date') && v.date ? { date: v.date } : {}),
          ...(v.note?.trim() ? { note: v.note.trim() } : {}),
        },
      });
      toast.done(t(`settlement.actions.${action}.done`, vars));
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, ['issuedOn', 'expiresOn', 'reference', 'amount', 'date']);
        setProblem(e);
      }
    }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        size={f.length > 1 ? 'form' : undefined}
        title={t(`settlement.actions.${action}.title`, vars)}
        description={t(`settlement.actions.${action}.body`, vars)}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => void submit()}>
              {t(`settlement.actions.${action}.label`)}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
          {f.includes('issuedOn') ? (
            <div className="grid grid-cols-2 gap-4">
              <DateField control={form.control} name="issuedOn" label={t('settlement.fields.issuedOn')} max={today} />
              <DateField control={form.control} name="expiresOn" label={t('settlement.fields.expiresOn')} help={t('settlement.fields.expiresHelp')} />
            </div>
          ) : null}
          {f.includes('reference') ? <TextField control={form.control} name="reference" label={action === 'issue_mc' ? t('settlement.fields.mcNumber') : t('settlement.fields.letterReference')} optional={action !== 'issue_mc'} dir="ltr" /> : null}
          {f.includes('amount') ? <MoneyField control={form.control} name="amount" label={action === 'record_settled' ? t('settlement.fields.settledAmount') : t('settlement.fields.liability')} optional={action !== 'record_liability'} /> : null}
          {f.includes('date') ? <DateField control={form.control} name="date" label={action === 'issue_mc' ? t('settlement.fields.mcDate') : t('settlement.fields.date')} max={today} optional={action === 'issue_mc'} /> : null}
          {f.includes('file') ? (
            <FileField
              label={action === 'record_clearance' ? t('settlement.fields.clearanceFile') : action === 'issue_mc' ? t('settlement.fields.mcFile') : t('settlement.fields.letterFile')}
              purpose="settlement"
              optional
              help={t('settlement.fields.fileHelp')}
              onChange={setFile}
            />
          ) : null}
          <TextareaField control={form.control} name="note" label={t('settlement.fields.note')} optional rows={2} />
          <button type="submit" hidden />
        </form>
      </DialogContent>
    </Dialog>
  );
}
